/**
 * Opción múltiple: el primer tipo de pregunta de Clica.
 *
 * **Este es el único archivo del repositorio donde aparece su identificador**, junto con el mapa
 * de registro. `scripts/check-question-types.sh` lo comprueba en cada ejecución del CI: si alguien
 * escribe un condicional por tipo en el motor o importa este esquema desde un componente genérico,
 * el guardián lo detecta.
 */
import { z } from 'zod';
import { defineQuestionType, } from './contract.js';
import { MAX_OPTION_LENGTH, MAX_PROMPT_LENGTH } from '../constants.js';
/** Entre dos y cuatro opciones (RF-006). No depende del tamaño de ninguna paleta de color. */
export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 4;
export const multipleChoiceOptionSchema = z.object({
    position: z.number().int().nonnegative(),
    /** Nulo es legítimo mientras se escribe: el autoguardado escribe borradores a medias. */
    text_md: z.string().nullable(),
    is_correct: z.boolean(),
});
export const multipleChoiceConfigSchema = z.object({
    prompt_md: z.string().nullable(),
    options: z.array(multipleChoiceOptionSchema),
});
const isBlank = (value) => value === null || value.trim() === '';
/**
 * ¿Se puede **jugar** esta pregunta?
 *
 * Devuelve todos los problemas, no solo el primero: el profesor debe poder arreglarlos de una vez
 * en lugar de descubrirlos de uno en uno (RF-018).
 */
function validateQuestion(config) {
    const problems = [];
    if (isBlank(config.prompt_md))
        problems.push({ reason: 'emptyPrompt' });
    // El límite es de dominio y no de interfaz: un enunciado que no cabe en la pizarra no se
    // arregla con una pantalla más grande.
    if ((config.prompt_md ?? '').length > MAX_PROMPT_LENGTH) {
        problems.push({ reason: 'promptTooLong' });
    }
    const { options } = config;
    if (options.length < MIN_OPTIONS)
        problems.push({ reason: 'tooFewOptions' });
    if (options.length > MAX_OPTIONS)
        problems.push({ reason: 'tooManyOptions' });
    for (const option of options) {
        if (isBlank(option.text_md)) {
            problems.push({ reason: 'emptyOption', optionPosition: option.position });
        }
        if ((option.text_md ?? '').length > MAX_OPTION_LENGTH) {
            problems.push({ reason: 'optionTooLong', optionPosition: option.position });
        }
    }
    const correct = options.filter((option) => option.is_correct).length;
    if (correct === 0)
        problems.push({ reason: 'noCorrectOption' });
    if (correct > 1)
        problems.push({ reason: 'severalCorrectOptions' });
    return problems;
}
export const multipleChoice = defineQuestionType({
    id: 'multiple_choice',
    configSchema: multipleChoiceConfigSchema,
    validateQuestion,
});
//# sourceMappingURL=multiple-choice.js.map